import { ETypographyIntents } from '@src/typings';
import React, { useState } from 'react';
import { HiOutlineChevronDown } from 'react-icons/hi2';
import {
    ParsedCountry,
    PhoneInputProps,
    usePhoneInput,
} from 'react-international-phone';
import 'react-international-phone/style.css';
import { twMerge } from 'tailwind-merge';
import { useTheme, withTheme } from '../ThemeProvider';
import { Typography } from '../Typography';
import { StyledCountrySelectorDropdown, StyledPhoneInput } from './styles';

export interface IPhoneInput extends PhoneInputProps {
    label?: string | React.ReactNode;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    help?: string;
    theme?: any; // Add theme prop for withTheme HOC
    buttonClassName?: string;
    dialCodeClassName?: string;
}

const PhoneInputComponent = React.forwardRef<any, IPhoneInput>(
    (props: IPhoneInput, ref) => {
        const {
            label,
            required,
            error,
            help,
            disabled,
            buttonClassName = '',
            dialCodeClassName = '',
            value,
            ...rest
        } = props;
        const [open, setOpen] = useState(false);
        const { country, setCountry } = usePhoneInput({ value: value });
        const { theme } = useTheme();
        const [phone, setPhone] = useState(value);

        function handleSelect(country: ParsedCountry) {
            setCountry(country.iso2, { focusOnInput: true });
            setPhone(country.dialCode);
            setOpen(false);
        }

        function handleClose() {
            setOpen(false);
        }

        function handleOpen() {
            if (!disabled) setOpen(true);
        }

        const getSelectorStyle = () => {
            const styles: React.CSSProperties = {};

            styles.backgroundColor = theme.colors.secondary.dark.contentBg;

            if (disabled) {
                styles.cursor = 'not-allowed';
                styles.opacity = '0.6';
            }

            return styles;
        };

        const getTextStyle = () => {
            return {
                color: theme.colors.primary.main.contentBg,
            };
        };

        const getIconStyle = () => {
            return {
                color: theme.colors.primary.main.contentBg,
            };
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                handleOpen();
            }
        };

        return (
            <div className="relative flex items-center">
                <StyledPhoneInput
                    onChange={(_, { country: newCountry }) => {
                        setCountry(newCountry.iso2, { focusOnInput: true });
                        setPhone(newCountry.dialCode);
                    }}
                    value={phone}
                    forceDialCode={false}
                    variant={
                        disabled ? 'disabled' : error ? 'error' : 'default'
                    } // Pass variant instead of boolean
                    {...rest}
                    disabled={disabled}
                    ref={ref}
                />
                <StyledCountrySelectorDropdown
                    show={open}
                    selectedCountry={country.iso2}
                    onSelect={handleSelect}
                    onClose={handleClose}
                />
                <div
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onClick={handleOpen}
                    onKeyDown={handleKeyDown}
                    aria-label="Select country code"
                    className={twMerge(
                        'm-1 absolute rounded-lg w-28 h-[35px] cursor-pointer',
                        disabled ? 'cursor-not-allowed' : '',
                        buttonClassName
                    )}
                    style={getSelectorStyle()}
                >
                    <div className="flex justify-end items-center h-full">
                        <Typography
                            as="p"
                            intent={ETypographyIntents.Regular}
                            className={twMerge('ml-11', dialCodeClassName)}
                            style={getTextStyle()}
                        >
                            +{country.dialCode}
                        </Typography>
                        <p className="mr-2.5">
                            <HiOutlineChevronDown
                                className="flex-1"
                                style={getIconStyle()}
                            />
                        </p>
                    </div>
                </div>
                {error && (
                    <span
                        className="w-full font-normal text-xs text-left mt-1"
                        style={{ color: theme.colors.error.main.text }}
                    >
                        {error}
                    </span>
                )}
            </div>
        );
    }
);

PhoneInputComponent.displayName = 'PhoneInputComponent';

export const PhoneInput = withTheme(PhoneInputComponent);
